import { useState } from "react";
import { Camera, Plus, X } from "lucide-react";
import PhotoGallery from "../atm/PhotoGallery";
import { downloadUrl } from "../../lib/api";

export const MAX_ENTRY_PHOTOS = 4;
export const PHOTO_KINDS = [["before", "İş Öncesi"], ["after", "İş Sonrası"]];
const kindLabel = (k) => (PHOTO_KINDS.find(([key]) => key === k) || [])[1] || "";
const objUrl = (f) => { if (!f.__url) f.__url = URL.createObjectURL(f); return f.__url; };
export const imageSrc = (entryId, im) => downloadUrl(`/entries/${entryId}/image/${im.id}`);

// Hizmet kaydı fotoğrafları: iş öncesi / iş sonrası (tür başına en fazla 4) — bekleyen dosyalar + (düzeltmede) kayıtlı görseller
export default function EntryPhotos({ pending, onChange, existing = [], onDeleteExisting, entryId, disabled, testid = "entry-photos" }) {
  const [gallery, setGallery] = useState(null);
  const items = (k) => [
    ...existing.filter((im) => im.kind === k).map((im) => ({ src: imageSrc(entryId, im), im })),
    ...(pending?.[k] || []).map((f, idx) => ({ src: objUrl(f), idx })),
  ];
  const all = PHOTO_KINDS.flatMap(([k, label]) => items(k).map((it) => ({ src: it.src, alt: label })));
  const add = (k, list) => {
    const files = [...(list || [])].filter((f) => f.type.startsWith("image/"));
    const cur = pending?.[k] || [];
    const room = MAX_ENTRY_PHOTOS - existing.filter((im) => im.kind === k).length - cur.length;
    if (room > 0 && files.length) onChange(k, [...cur, ...files.slice(0, room)]);
  };
  const removePending = (k, idx) => onChange(k, (pending?.[k] || []).filter((_, j) => j !== idx));
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid={testid}>
      {PHOTO_KINDS.map(([k, label]) => {
        const list = items(k);
        const full = list.length >= MAX_ENTRY_PHOTOS;
        return (
          <div key={k} className="flex items-center gap-1.5" data-testid={`${testid}-${k}`}>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-[4.5rem]">{label}</span>
            {list.map((it, i) => (
              <div key={it.im ? it.im.id : `p${i}`} className="relative w-9 h-9 rounded-md overflow-hidden border border-slate-300 bg-slate-100 shrink-0">
                <img src={it.src} alt={label} onClick={() => setGallery(all.findIndex((a) => a.src === it.src))} className="w-full h-full object-cover cursor-zoom-in" data-testid={`${testid}-${k}-thumb-${i}`} />
                {!disabled && <button type="button" onClick={() => (it.im ? onDeleteExisting?.(it.im) : removePending(k, it.idx))} aria-label="Fotoğrafı kaldır" data-testid={`${testid}-${k}-remove-${i}`} className="absolute top-0 right-0 p-0.5 rounded-bl bg-black/60 text-white hover:bg-black/80"><X className="w-2.5 h-2.5" /></button>}
              </div>
            ))}
            <label className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-dashed text-[11px] font-semibold ${full || disabled ? "border-slate-300 text-slate-400 cursor-not-allowed bg-slate-50" : "border-sky-500 text-sky-800 hover:bg-sky-50 cursor-pointer"}`} data-testid={`${testid}-${k}-add`}>
              {list.length ? <Plus className="w-3 h-3" /> : <Camera className="w-3 h-3" />} {full ? `En fazla ${MAX_ENTRY_PHOTOS}` : "Fotoğraf"}
              <input type="file" accept="image/*" multiple disabled={full || disabled} className="hidden" onChange={(e) => { add(k, e.target.files); e.target.value = ""; }} data-testid={`${testid}-${k}-file`} />
            </label>
          </div>
        );
      })}
      {gallery != null && gallery >= 0 && <PhotoGallery images={all} start={gallery} title="Hizmet fotoğrafları" onClose={() => setGallery(null)} />}
    </div>
  );
}

// Kayıt listesinde küçük resimler (en fazla 4 + sayaç); tıklayınca sayfa içi galeri
export function EntryThumbs({ entry }) {
  const [start, setStart] = useState(null);
  const images = entry.images || [];
  if (!images.length) return null;
  const all = images.map((im) => ({ src: imageSrc(entry.id, im), alt: `${kindLabel(im.kind)} — ${entry.service_name}` }));
  return (
    <div className="flex items-center gap-1 mt-1" data-testid={`entry-thumbs-${entry.id}`}>
      {images.slice(0, 4).map((im, i) => (
        <button type="button" key={im.id} onClick={() => setStart(i)} title={kindLabel(im.kind)} data-testid={`entry-thumb-${entry.id}-${i}`} className="relative w-7 h-7 rounded overflow-hidden border border-slate-300 hover:ring-2 hover:ring-sky-400">
          <img src={all[i].src} alt={all[i].alt} className="w-full h-full object-cover" />
          <span className={`absolute bottom-0 inset-x-0 text-[7px] font-bold text-white text-center leading-[9px] ${im.kind === "after" ? "bg-emerald-600/90" : "bg-slate-700/90"}`}>{im.kind === "after" ? "SON" : "ÖN"}</span>
        </button>
      ))}
      {images.length > 4 && <span className="text-[10px] text-slate-500 font-semibold">+{images.length - 4}</span>}
      {start != null && <PhotoGallery images={all} start={start} title={`${entry.service_name} — ${entry.location_name}`} onClose={() => setStart(null)} />}
    </div>
  );
}
